const EXTENSION_SESSION_TOKEN_HASH_KEY = 'phoebeExtensionSessionToken';
const EXTENSION_SESSION_TOKEN_STORAGE_KEY = 'phoebe-extension-session-token';

// The sidepanel iframe can run in a storage-partitioned context where
// `sessionStorage` is intermittently unavailable. Mirror the token in memory so
// it survives revalidations within the frame's lifetime even when persistence
// is blocked. Without this, a revalidation re-reads an empty store, falls back
// to a tokenless `whoami`, and 401s — the repeating sidepanel 401 loop.
let inMemorySessionToken: string | null = null;

function isExtensionSessionContext(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }

    return window.location.pathname === '/extension';
}

function persistExtensionSessionToken(token: string): void {
    inMemorySessionToken = token;
    try {
        window.sessionStorage.setItem(
            EXTENSION_SESSION_TOKEN_STORAGE_KEY,
            token,
        );
    } catch {
        // sessionStorage may be unavailable (partitioned third-party frame);
        // the in-memory copy above keeps the token usable regardless.
    }
}

function removeExtensionSessionTokenHash(): void {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    hashParams.delete(EXTENSION_SESSION_TOKEN_HASH_KEY);
    const nextHash = hashParams.toString();
    const nextUrl =
        `${window.location.pathname}${window.location.search}` +
        (nextHash.length > 0 ? `#${nextHash}` : '');
    window.history.replaceState(window.history.state, '', nextUrl);
}

function getExtensionSessionTokenFromHash(): string | null {
    if (!window.location.hash.startsWith('#')) {
        return null;
    }

    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const token = hashParams.get(EXTENSION_SESSION_TOKEN_HASH_KEY);
    if (!token) {
        return null;
    }

    persistExtensionSessionToken(token);
    removeExtensionSessionTokenHash();
    return token;
}

function getPersistedExtensionSessionToken(): string | null {
    try {
        const stored = window.sessionStorage.getItem(
            EXTENSION_SESSION_TOKEN_STORAGE_KEY,
        );
        if (stored) {
            return stored;
        }
    } catch {
        // sessionStorage may be unavailable (partitioned third-party frame).
    }

    return inMemorySessionToken;
}

export function getExtensionSessionToken(): string | null {
    if (typeof window === 'undefined') {
        return null;
    }

    // The token only arrives in the URL hash on the `/extension` entry, so read
    // the hash there. Once persisted, honor it on every route: deep-linked
    // sub-views (e.g. `/outreaches/:id`) navigate off `/extension`, and refusing
    // the token there makes the web app fall back to partitioned third-party
    // cookies, which `whoami`-401 in the embedded sidepanel.
    const tokenFromHash = isExtensionSessionContext()
        ? getExtensionSessionTokenFromHash()
        : null;
    if (tokenFromHash) {
        return tokenFromHash;
    }

    return getPersistedExtensionSessionToken();
}

export function clearExtensionSessionToken(): void {
    inMemorySessionToken = null;
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.sessionStorage.removeItem(EXTENSION_SESSION_TOKEN_STORAGE_KEY);
    } catch {
        // sessionStorage may be unavailable.
    }
}

// Deliberately no helper that puts this token in a URL. It is an HTTP bearer
// credential, sent by `api_client` as an `Authorization` header. The `?token`
// query parameter on `/events/user/{user_id}/ws` means a single-use ws ticket,
// so a session token there authenticated nobody. Sockets mint a ticket via
// `createOrganizationWebsocketTicket` instead.
